import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CustomerPicker, { type PickedCustomer } from '../components/CustomerPicker';
import ItemBuilder, { type BuilderResult } from '../components/ItemBuilder';
import NumInput from '../components/NumInput';
import QuoteComments from '../components/QuoteComments';
import SendQuote from '../components/SendQuote';
import { ErrorNote, Field, StatusPill } from '../components/ui';
import { date, money, percent } from '../lib/format';
import { statusesFor, useLookups } from '../lib/lookups';
import { supabase } from '../lib/supabase';
import { quoteTotals, round2, type Build } from '../pricing/itemBuilder';

interface Line {
  key: string;
  description: string;
  qty: number;
  unit_sell: number;
  total_sell: number;
  total_cost: number;
  tax_id: string | null;
  build: Build | null;
}

interface QuoteHead {
  id?: string;
  number?: number;
  status_id: string | null;
  contact_id: string | null;
  deadline: string | null;
  reference: string;
  description: string;
  allow_comments: boolean;
  accept_online: boolean;
  hide_total: boolean;
  created_at?: string;
  public_token?: string;
}

const key = () => Math.random().toString(36).slice(2);
const emptyHead: QuoteHead = { status_id: null, contact_id: null, deadline: null, reference: '', description: '',
  allow_comments: true, accept_online: true, hide_total: false };

export default function QuoteEditor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { statuses, taxes, loaded } = useLookups();
  const quoteStatuses = statusesFor(statuses, 'quote');
  const defaultTax = taxes.find((t) => t.is_default) ?? taxes[0];

  const [head, setHead] = useState<QuoteHead>(emptyHead);
  const [customer, setCustomer] = useState<PickedCustomer | null>(null);
  const [contacts, setContacts] = useState<{ id: string; label: string }[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [jobs, setJobs] = useState<{ id: string; number: number }[]>([]);
  const [builderFor, setBuilderFor] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!id);
  const [sending, setSending] = useState(false);
  const [reload, setReload] = useState(0);

  // Load an existing quote
  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      const [q, items, j] = await Promise.all([
        supabase.from('quotes').select('*, customers(id, name, min_markup)').eq('id', id).single(),
        supabase.from('quote_items').select('*').eq('quote_id', id).eq('archived', false).order('sort'),
        supabase.from('jobs').select('id, number').eq('quote_id', id),
      ]);
      if (q.error) { setError(q.error.message); setLoading(false); return; }
      const d: any = q.data;
      setHead({ id: d.id, number: d.number, status_id: d.status_id, contact_id: d.contact_id, deadline: d.deadline,
        reference: d.reference ?? '', description: d.description ?? '', allow_comments: d.allow_comments,
        accept_online: d.accept_online, hide_total: d.hide_total, created_at: d.created_at, public_token: d.public_token });
      setCustomer(d.customers ? { id: d.customers.id, name: d.customers.name, min_markup: d.customers.min_markup } : null);
      setLines(((items.data as any[]) ?? []).map((it) => ({ key: key(), description: it.description ?? '', qty: Number(it.qty),
        unit_sell: Number(it.unit_sell), total_sell: Number(it.total_sell), total_cost: Number(it.total_cost),
        tax_id: it.tax_id, build: it.build })));
      setJobs((j.data as any[]) ?? []);
      setDirty(false);
      setLoading(false);
    })();
  }, [id, reload]);

  // New quote: preselect customer from ?customer=
  useEffect(() => {
    const cid = params.get('customer');
    if (id || !cid) return;
    supabase.from('customers').select('id, name, min_markup').eq('id', cid).single()
      .then(({ data }) => data && setCustomer(data as PickedCustomer));
  }, [id, params]);

  // New quote: start with one empty line once taxes are known
  useEffect(() => {
    if (!id && loaded && lines.length === 0) setLines([blankLine()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, loaded]);

  // Contacts for the chosen customer
  useEffect(() => {
    if (!customer) { setContacts([]); return; }
    supabase.from('contacts').select('id, first_name, last_name, email').eq('customer_id', customer.id).eq('archived', false)
      .order('is_primary', { ascending: false })
      .then(({ data }) => {
        const list = ((data as any[]) ?? []).map((c) => ({ id: c.id, label: [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email }));
        setContacts(list);
        setHead((h) => (h.contact_id && list.some((c) => c.id === h.contact_id) ? h : { ...h, contact_id: list[0]?.id ?? null }));
      });
  }, [customer?.id]);

  const blankLine = (): Line => ({ key: key(), description: '', qty: 1, unit_sell: 0, total_sell: 0, total_cost: 0, tax_id: defaultTax?.id ?? null, build: null });

  const taxRate = (taxId: string | null) => taxes.find((t) => t.id === taxId)?.rate ?? 0;
  const totals = useMemo(() => quoteTotals(lines.map((l) => ({ total_sell: l.total_sell, tax_rate: taxRate(l.tax_id) }))), [lines, taxes]);
  const cost = round2(lines.reduce((s, l) => s + (l.total_cost || 0), 0));
  const margin = totals.subtotal > 0 ? ((totals.subtotal - cost) / totals.subtotal) * 100 : null;

  const touch = () => { setDirty(true); setNotice(null); };
  const setH = (patch: Partial<QuoteHead>) => { setHead((h) => ({ ...h, ...patch })); touch(); };
  const setLine = (i: number, patch: Partial<Line>) => {
    setLines((ls) => ls.map((l, j) => {
      if (j !== i) return l;
      const n = { ...l, ...patch };
      if (!n.build && ('qty' in patch || 'unit_sell' in patch)) n.total_sell = round2((n.qty || 0) * (n.unit_sell || 0));
      return n;
    }));
    touch();
  };
  const moveLine = (i: number, d: -1 | 1) => {
    setLines((ls) => { const a = [...ls]; const j = i + d; if (j < 0 || j >= a.length) return ls; [a[i], a[j]] = [a[j], a[i]]; return a; });
    touch();
  };

  const applyBuild = (i: number, r: BuilderResult) => {
    setLine(i, { build: r.build, description: r.description, qty: r.result.qty, unit_sell: r.result.unit_sell,
      total_sell: r.result.total_sell, total_cost: r.result.total_cost });
    setBuilderFor(null);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    const p = {
      id: head.id ?? null, customer_id: customer?.id ?? null, contact_id: head.contact_id, status_id: head.status_id,
      deadline: head.deadline, reference: head.reference, description: head.description,
      allow_comments: head.allow_comments, accept_online: head.accept_online, hide_total: head.hide_total,
      subtotal: totals.subtotal, tax_total: totals.tax, total: totals.total,
      items: lines.filter((l) => l.description.trim() || l.total_sell).map((l, i) => ({
        quote_option: 'A', sort: i, description: l.description, qty: l.qty, unit_sell: l.unit_sell,
        total_sell: l.total_sell, total_cost: l.total_cost, tax_id: l.tax_id, build: l.build })),
    };
    const { data, error } = await supabase.rpc('save_quote', { p });
    setBusy(false);
    if (error) return setError(error.message);
    setDirty(false);
    if (!head.id) nav(`/quotes/${data}`, { replace: true });
    else setNotice('Saved');
  };

  const convert = async () => {
    if (dirty) return setError('Save your changes before converting to a job.');
    if (!head.id) return;
    if (!window.confirm(`Convert quote ${head.number} to a job?`)) return;
    setBusy(true);
    const { data, error } = await supabase.rpc('convert_quote_to_job', { p_quote: head.id, p_option: 'A' });
    setBusy(false);
    if (error) return setError(error.message);
    nav(`/jobs/${data}`);
  };

  const copyLink = async () => {
    if (!head.public_token) return;
    await navigator.clipboard.writeText(`${window.location.origin}/q/${head.public_token}`);
    setNotice('Customer link copied');
  };
  const openSend = () => {
    if (dirty) return setError('Save your changes before sending.');
    setError(null);
    setSending(true);
  };

  const status = quoteStatuses.find((s) => s.id === head.status_id);
  if (loading) return <div className="empty">Loading quote…</div>;

  return (
    <div className="page">
      <div className="page-head">
        <Link to="/quotes" className="muted">← SalesBoard</Link>
        <h1>{head.number ? `Quote ${head.number}` : 'New quote'}</h1>
        {status && <StatusPill label={status.label} colour={status.colour} />}
        {head.created_at && <span className="muted small">created {date(head.created_at)}</span>}
        {jobs.map((j) => <Link key={j.id} className="tag" to={`/jobs/${j.id}`}>Job {j.number}</Link>)}
        <span className="spacer" />
        {dirty && <span className="muted small">Unsaved changes</span>}
        {notice && <span className="ok small">{notice}</span>}
        {head.id && head.public_token && <a className="btn" href={`/q/${head.public_token}?preview=1`} target="_blank" rel="noreferrer">Preview / PDF</a>}
        {head.id && <button onClick={copyLink}>Copy link</button>}
        {head.id && <button onClick={openSend}>Send</button>}
        {head.id && jobs.length === 0 && <button onClick={convert} disabled={busy}>Convert to job</button>}
        <button className="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
      <ErrorNote error={error} />

      <div className="editor" style={{ marginTop: error ? 12 : 0 }}>
        <div className="stack">
          <div className="card card-pad">
            <div className="grid3">
              <Field label="Customer"><CustomerPicker value={customer} onPick={(c) => { setCustomer(c); touch(); }} /></Field>
              <Field label="Contact">
                <select value={head.contact_id ?? ''} onChange={(e) => setH({ contact_id: e.target.value || null })} disabled={!contacts.length}>
                  {!contacts.length && <option value="">{customer ? 'No contacts' : 'Pick a customer first'}</option>}
                  {contacts.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="Reference"><input value={head.reference} onChange={(e) => setH({ reference: e.target.value })} placeholder="e.g. Tote Bags" /></Field>
              <Field label="Status">
                <select value={head.status_id ?? ''} onChange={(e) => setH({ status_id: e.target.value || null })}>
                  {!head.id && <option value="">Draft</option>}
                  {quoteStatuses.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Deadline"><input type="date" value={head.deadline ?? ''} onChange={(e) => setH({ deadline: e.target.value || null })} /></Field>
              {customer && <div className="small muted" style={{ alignSelf: 'end', paddingBottom: 8 }}>
                <Link to={`/customers/${customer.id}`}>Open customer</Link>
                {customer.min_markup != null && <> · min markup {customer.min_markup}%</>}
              </div>}
            </div>
            <div style={{ marginTop: 12 }}>
              <Field label="Description (internal)"><textarea value={head.description} onChange={(e) => setH({ description: e.target.value })} /></Field>
            </div>
          </div>

          <div className="card">
            <table className="grid lines">
              <thead>
                <tr><th style={{ width: 34 }}></th><th>Description</th><th className="num" style={{ width: 70 }}>Qty</th>
                  <th className="num" style={{ width: 90 }}>Rate</th><th style={{ width: 104 }}>Tax</th>
                  <th className="num" style={{ width: 100 }}>Total</th><th style={{ width: 186 }}></th></tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.key}>
                    <td className="muted small">{i + 1}</td>
                    <td>
                      <textarea value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} placeholder="Line description" rows={3} />
                      {l.build && <div className="small muted" style={{ marginTop: 2 }}>
                        Built from {l.build.rows.length} parts · cost {money(l.total_cost)} · margin {percent(l.total_sell ? ((l.total_sell - l.total_cost) / l.total_sell) * 100 : 0)}
                      </div>}
                    </td>
                    <td><NumInput value={l.qty} disabled={!!l.build} onChange={(v) => setLine(i, { qty: v ?? 0 })} ariaLabel="Quantity" /></td>
                    <td><NumInput value={l.unit_sell} disabled={!!l.build} onChange={(v) => setLine(i, { unit_sell: v ?? 0 })} ariaLabel="Rate" /></td>
                    <td>
                      <select value={l.tax_id ?? ''} onChange={(e) => setLine(i, { tax_id: e.target.value || null })}>
                        {taxes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </td>
                    <td className="num"><b>{money(l.total_sell)}</b></td>
                    <td>
                      <div className="row" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        <button className="small" onClick={() => setBuilderFor(i)}>{l.build ? 'Edit build' : 'Build'}</button>
                        {l.build && <button className="ghost small" title="Unlink build (edit qty and rate by hand)" onClick={() => setLine(i, { build: null })}>⛓</button>}
                        <button className="ghost small" onClick={() => moveLine(i, -1)} aria-label="Move up">↑</button>
                        <button className="ghost small" onClick={() => moveLine(i, 1)} aria-label="Move down">↓</button>
                        <button className="ghost small danger" onClick={() => { setLines(lines.filter((_, j) => j !== i)); touch(); }} aria-label="Remove line">✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="row" style={{ padding: 12 }}>
              <button onClick={() => { setLines([...lines, blankLine()]); touch(); }}>+ Add line</button>
              <button onClick={() => { setLines([...lines, blankLine()]); setBuilderFor(lines.length); }}>+ Build item</button>
              <span className="grow" />
              <div className="totals">
                <div><span>Subtotal</span><span className="num">{money(totals.subtotal)}</span></div>
                <div><span>GST</span><span className="num">{money(totals.tax)}</span></div>
                <div className="grand"><span>Total</span><span className="num">{money(totals.total)}</span></div>
              </div>
            </div>
          </div>
        </div>

        <aside className="stack">
          <div className="card card-pad stack">
            <h3>Profit</h3>
            <div className="totals" style={{ width: '100%' }}>
              <div><span>Cost (built lines)</span><span className="num">{money(cost)}</span></div>
              <div><span>Sell (ex GST)</span><span className="num">{money(totals.subtotal)}</span></div>
              <div className="grand"><span>Gross margin</span><span className="num">{margin == null ? '—' : percent(margin, 2)}</span></div>
            </div>
            {lines.some((l) => !l.build && l.total_sell > 0) && <div className="muted small">Lines entered by hand have no cost, so margin counts only built lines’ costs.</div>}
          </div>
          <div className="card card-pad stack settings">
            <h3>Settings</h3>
            <label className="check"><input type="checkbox" checked={head.allow_comments} onChange={(e) => setH({ allow_comments: e.target.checked })} /> Allow comments</label>
            <label className="check"><input type="checkbox" checked={head.accept_online} onChange={(e) => setH({ accept_online: e.target.checked })} /> Accept online</label>
            <label className="check"><input type="checkbox" checked={head.hide_total} onChange={(e) => setH({ hide_total: e.target.checked })} /> Hide total</label>
          </div>
          {head.id && <QuoteComments quoteId={head.id} refreshKey={reload} />}
        </aside>
      </div>

      {sending && head.id && head.number && head.public_token && (
        <SendQuote quote={{ id: head.id, number: head.number, reference: head.reference, public_token: head.public_token,
          contact_id: head.contact_id, customer_name: customer?.name }}
          onClose={() => setSending(false)} onSent={() => { setNotice('Marked as sent'); setReload((n) => n + 1); }} />
      )}

      {builderFor != null && lines[builderFor] && (
        <ItemBuilder initial={lines[builderFor].build ?? undefined} description={lines[builderFor].description}
          customerMinMarkup={customer?.min_markup ?? null}
          onApply={(r) => applyBuild(builderFor, r)} onClose={() => setBuilderFor(null)} />
      )}
    </div>
  );
}
